import { useEffect, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import Spinner from "@atlaskit/spinner";

import { useAnalyticsClient } from "@/analytics/wac-analytics";
import {
    LayoutVersion,
    SignupBackgroundLayout,
    TwcOverlayTemplate
} from "@/components/signup/layouts";
import SiteActivation, {
    Dropdown,
    useSiteActivationContext
} from "@/components/signup/site-activation";
import {
    isProduction,
    TeamworkCollectionLogo
} from "@/components/signup/utils";
import { useUpliftableSites } from "@/components/user-context";
import {
    ExistingSite,
    GetSignupAccountOrgDetailsResponse,
    useBxpWebStargateClient
} from "@/libraries/bxp-web-stargate-client/src";
import { Button } from "@/libraries/design/components";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import { Heading } from "../../components";
import { SwitchYourSiteMessage } from "../components";

export const UpliftEligibleUser = () => {
    const twcSignupOverlayEnrolled = checkFeatureFlagGate(
        featureFlags.cfind5480TwcSignupOverlay
    );

    let content = (
        <SignupBackgroundLayout ProductLogo={<TeamworkCollectionLogo />}>
            <Heading>
                <FormattedMessage
                    id="signup.teamworkCollection.uplift.heading"
                    defaultMessage="Switch your site to Teamwork Collection"
                />
            </Heading>
            <SiteActivation UpliftEligibleUser={<UpliftPicker />} />
        </SignupBackgroundLayout>
    );

    if (twcSignupOverlayEnrolled) {
        content = (
            <TwcOverlayTemplate
                size="sm"
                version={LayoutVersion.V4}
                productLogo={<TeamworkCollectionLogo shouldShowBrand={false} />}
            >
                <Heading>
                    <FormattedMessage
                        id="signup.teamworkCollection.uplift.heading"
                        defaultMessage="Switch your site to Teamwork Collection"
                    />
                </Heading>
                <SiteActivation UpliftEligibleUser={<UpliftPicker />} />
            </TwcOverlayTemplate>
        );
    }

    return content;
};

const UpliftPicker = () => {
    const [loading, setLoading] = useState(true);
    const [orgs, setOrgs] = useState<GetSignupAccountOrgDetailsResponse[]>([]);
    const { sitePickerValue } = useSiteActivationContext();
    const potentiallyUpliftableSites = useUpliftableSites();
    const [upliftableSites, setUpliftableSites] = useState<ExistingSite[]>([]);
    const stargateClient = useBxpWebStargateClient();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const analyticsClient = useAnalyticsClient();

    const onSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!sitePickerValue) return;
        const cloudId = sitePickerValue.cloudId;
        const selectedOrg = orgs.find((org) =>
            org.organization?.siteIds?.includes(cloudId)
        );
        const orgId = selectedOrg?.organization?.id;
        const adminHubUrl = isProduction()
            ? "https://admin.atlassian.com"
            : "https://admin.stg.atlassian.com";
        const upgradeUrl = `${adminHubUrl}/o/${orgId}/atlassian-apps?openAddAppDrawer=TeamworkCollection`;

        createAnalyticsEvent({
            eventType: "ui",
            action: "submitted",
            actionSubject: "twcUpliftForm"
        }).fire();

        window.location.assign(upgradeUrl);
    };

    useEffect(() => {
        if (!potentiallyUpliftableSites) {
            return;
        }
        stargateClient
            .getAccountOrgs({})
            .then(async ({ accountOrgs }) => {
                if (!accountOrgs) {
                    return;
                }
                const orgIds = accountOrgs
                    .filter(({ isAdmin }) => isAdmin)
                    .map(({ id }) => id);
                const orgDetails = await Promise.all(
                    orgIds.map((orgId) =>
                        stargateClient.getSignupAccountOrgDetails({ orgId })
                    )
                );
                const eligibleCloudIds = orgDetails.flatMap(
                    (org) => org.organization?.siteIds ?? []
                );
                const eligibleSites = potentiallyUpliftableSites.filter(
                    (site) => eligibleCloudIds.includes(site.cloudId)
                );
                setOrgs(orgDetails);
                setUpliftableSites(eligibleSites);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [stargateClient, potentiallyUpliftableSites]);

    useEffect(() => {
        if (loading || !analyticsClient) {
            return;
        }
        createAnalyticsEvent({
            eventType: "ui",
            action: "viewed",
            actionSubject: "twcUpliftScreen",
            attributes: {
                eligibleSitesCount: upliftableSites.length
            }
        }).fire();
    }, [
        loading,
        analyticsClient,
        createAnalyticsEvent,
        upliftableSites.length
    ]);

    if (loading) {
        return (
            <div css={spinnerContainerCss}>
                <Spinner size="large" />
            </div>
        );
    }

    if (!upliftableSites.length) {
        return <SwitchYourSiteMessage />;
    }

    return (
        <form css={formCss} onSubmit={onSubmit}>
            <Dropdown sites={upliftableSites} />
            <Button type="submit">
                <FormattedMessage
                    id="siteActivation.nameSiteNewUser.continueButton"
                    defaultMessage="Continue"
                />
            </Button>
        </form>
    );
};

const formCss = css({
    display: "flex",
    flexDirection: "column",
    gap: 24,
    marginBlockStart: 32,
    width: "100%",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    button: {
        maxWidth: "none"
    }
});

const spinnerContainerCss = css({
    alignItems: "center",
    display: "flex",
    flexGrow: 1,
    justifyContent: "center",
    padding: 40
});
